import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { PlantCode } from "src/model/class/general classes/plant-code";
import {
  PlantCodesStoreAction,
  StorageNativeStoreAction,
} from "src/components/shared-components/enum/enumStoreAction";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { PlantCodeService } from "src/service/plant-code.service";
import {
  plantCodesCompleteSubscribeAddressAction,
  plantCodesLoadErrorAction,
  plantCodesLoadErrorHandlingAction,
  plantCodesLoadListFromApiAction,
  plantCodesLoadSuccessAction,
  plantCodesStartSubscribeAddressAction,
  plantCodesUnsetKeyStorage,
} from "../actions/plant-code.action";
import { currentUserData } from "../selectors/authentication.selectors";
import { fullPlantCodesData } from "../selectors/plant-code.selectors";

/**
 * Handle generic side ngrx effect from area action
 */
@Injectable()
export class PlantCodeEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store,
    private _plantCodeService: PlantCodeService
  ) { }

  /**
   * Effect who catch main event and unset specific server key storage
   */
  public plantCodesUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      map((action) => plantCodesUnsetKeyStorage())
    )
  );

  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public plantCodesStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(PlantCodesStoreAction.PLANT_CODES_LOAD_INIT_START),
      withLatestFrom(this._store.select(currentUserData)),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        return plantCodesLoadListFromApiAction();
      })
    )
  );

  /**
   * Load full plant-codes lists effect
   */
  public plantCodesLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(PlantCodesStoreAction.PLANT_CODES_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._plantCodeService.getPlantsCodes().pipe(
          switchMap((plantCodesObject: PlantCode) => {
            return [
              plantCodesLoadSuccessAction({ plantCodesObject }),
              plantCodesStartSubscribeAddressAction({
                listPlantCodeGeneric: plantCodesObject.plantCodeGeneric,
              }),
            ];
          }),
          catchError((error: HttpErrorResponse) => {
            return of(plantCodesLoadErrorHandlingAction(error));
          })
        );
      })
    )
  );

  /**
   * Effect handling error in plant-codes lists loading
   */
  public plantCodesStartErrorHandlingEffects = createEffect(() =>
    this._actions$.pipe(
      ofType(PlantCodesStoreAction.PLANT_CODES_LOAD_ERROR_HANDLING),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(fullPlantCodesData)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((action) => {
        return plantCodesLoadErrorAction();
      })
    )
  );

  /**
   * Effect handling error in plant-codes lists loading
   */
  public plantCodesStartAddressSubscribeEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(PlantCodesStoreAction.PLANT_CODES_SUBSCRIBE_ADDRESS_CHANGE_START),
      withLatestFrom(this._store.select(currentUserData)),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      tap((action) => this._plantCodeService.subscribePlantCodeAddresses()),
      map(() => plantCodesCompleteSubscribeAddressAction())
    )
  );
}
